import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputFieldDate,
  MultiSelectDropdown,
  MultiSelectDropdownOptions
} from 'qampo-ui-library';
import { InputField, Modal } from '../UI';
import { Button } from '../UI/base/Button';
import modalStyles from '../UI/Modal.module.css';
import type { CreateStaffRequest, StaffProfessionResponse, StaffResponse } from '../../api/generated';
import { StaffAvailabilityEditor } from './StaffAvailabilityEditor';
import {
  createDefaultAvailabilityForm,
  formToStaffAvailability,
  getInvalidTimeRangeDays,
  staffAvailabilityToForm
} from './staffScheduleHelpers';
import type {
  AvailabilityForm,
  DayKey,
  WeekParity,
  WeekScheduleForm
} from './staffScheduleConstants';
import styles from './StaffFormModal.module.css';

interface StaffFormModalProps {
  staff: StaffResponse | null;
  competences: StaffProfessionResponse[];
  onSave: (payload: CreateStaffRequest) => void | Promise<void>;
  onClose: () => void;
}

interface StaffFormErrors {
  fullName?: string;
  startDate?: string;
  endDate?: string;
  days: Record<WeekParity, Partial<Record<DayKey, string>>>;
}

const NO_DAY_ERRORS: StaffFormErrors['days'] = { evenWeeks: {}, oddWeeks: {} };

const today = (): string => new Date().toLocaleDateString('en-CA');

export const StaffFormModal = ({ staff, competences, onSave, onClose }: StaffFormModalProps) => {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(staff?.fullName ?? '');
  const [selectedIds, setSelectedIds] = useState<string[]>(staff?.staffProfessionIds ?? []);
  const [availability, setAvailability] = useState<AvailabilityForm>(
    staff ? staffAvailabilityToForm(staff.availability) : createDefaultAvailabilityForm()
  );
  const [startDate, setStartDate] = useState(staff?.startDate ?? today());
  const [endDate, setEndDate] = useState(staff?.endDate ?? '');
  const [errors, setErrors] = useState<StaffFormErrors>({ days: NO_DAY_ERRORS });

  const selectedCompetences = competences.filter((c) => selectedIds.includes(c.id));

  const toggleCompetence = (competence: StaffProfessionResponse) => {
    setSelectedIds((prev) =>
      prev.includes(competence.id)
        ? prev.filter((id) => id !== competence.id)
        : [...prev, competence.id]
    );
  };

  const toDayErrors = (week: WeekScheduleForm): Partial<Record<DayKey, string>> => {
    const dayErrors: Partial<Record<DayKey, string>> = {};
    for (const day of getInvalidTimeRangeDays(week)) {
      dayErrors[day] = t('config-staff-error-time-range');
    }
    return dayErrors;
  };

  const validate = (): boolean => {
    const next: StaffFormErrors = {
      days: {
        evenWeeks: toDayErrors(availability.evenWeeks),
        oddWeeks: availability.isAlternating ? toDayErrors(availability.oddWeeks) : {}
      }
    };
    if (!fullName.trim()) next.fullName = t('config-staff-error-name');
    if (!startDate) next.startDate = t('config-staff-error-start-date');
    if (endDate && startDate && endDate <= startDate) {
      next.endDate = t('config-staff-error-end-after-start');
    }
    setErrors(next);
    return (
      !next.fullName &&
      !next.startDate &&
      !next.endDate &&
      Object.keys(next.days.evenWeeks).length === 0 &&
      Object.keys(next.days.oddWeeks).length === 0
    );
  };

  // Return the onSave promise so the Save button auto-disables until it settles.
  const handleSave = (): void | Promise<void> => {
    if (!validate()) return;
    return onSave({
      fullName: fullName.trim(),
      staffProfessionIds: selectedIds,
      availability: formToStaffAvailability(availability),
      startDate,
      ...(endDate ? { endDate } : {})
    });
  };

  return (
    <Modal
      title={staff ? t('config-staff-edit') : t('config-staff-create')}
      onClose={onClose}
      width={560}
      footer={
        <div className={modalStyles.footerRow}>
          <Button type="secondary" onClick={onClose}>
            {t('save-modal-cancel')}
          </Button>
          <Button type="primary" onClick={handleSave}>
            {t('config-iv-save')}
          </Button>
        </div>
      }
    >
      <div className={styles.form}>
        <InputField
          label={t('config-staff-name')}
          required
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={errors.fullName}
        />

        <MultiSelectDropdown<StaffProfessionResponse>
          label={t('config-staff-competences')}
          placeholder={t('config-staff-competences-placeholder')}
          selectedOptions={selectedCompetences}
          displayOptionName={(c) => c.name}
          onRemoveOption={(c) => toggleCompetence(c)}
        >
          <MultiSelectDropdownOptions<StaffProfessionResponse>
            options={competences}
            selectedOptions={selectedCompetences}
            handleSelectOption={toggleCompetence}
            getKey={(c) => c.id}
            displayOption={(c) => c.name}
          />
        </MultiSelectDropdown>

        <div className={styles.section}>
          <span className={styles.sectionLabel}>{t('config-staff-schedule')}</span>
          <StaffAvailabilityEditor
            value={availability}
            dayErrors={errors.days}
            onChange={setAvailability}
          />
        </div>

        <div className={styles.dateRow}>
          <InputFieldDate
            label={t('config-staff-start-date')}
            required
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            error={Boolean(errors.startDate)}
            helperText={errors.startDate}
          />
          <InputFieldDate
            label={t('config-staff-end-date')}
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            error={Boolean(errors.endDate)}
            helperText={errors.endDate}
          />
        </div>
      </div>
    </Modal>
  );
};
